/**
 * ModoCampoAdmin — UI mobile-first para Carlo em campo.
 * Rotas: /campo, /campo/foto, /campo/etapa, /campo/aprovacoes
 *
 * Usa JWT do CPS admin (já logado). Foto rápida e atualização de etapa
 * publicam DIRETO (sem fila). Aprovações usa a fila partner_updates.
 */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  bg: "#0a0a0a", bg2: "#161616", bg3: "#222", ink: "#f5f5f5", ink2: "#cdc8be",
  ink3: "#8a857a", gold: "#c4a05a", line: "#2a2a2a", danger: "#c47a7a", ok: "#5fa86c",
};

const Big = ({ children, color = "gold", onClick, disabled, badge, ...rest }) => {
  const c = {
    gold: { bg: "linear-gradient(180deg,#d4b27a 0%,#c4a05a 100%)", color: "#0a0a0a" },
    green: { bg: T.ok, color: "#fff" },
    red: { bg: T.danger, color: "#fff" },
    ghost: { bg: "transparent", color: T.gold, border: `1px solid ${T.line}` },
  }[color];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        position: "relative", minHeight: 96, padding: "20px 24px", fontSize: 22, fontWeight: 700,
        letterSpacing: ".05em", textTransform: "uppercase",
        border: c.border || "transparent", background: c.bg, color: c.color,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 14, width: "100%",
        WebkitTapHighlightColor: "transparent", transition: "transform .12s",
      }}
      onTouchStart={e => e.currentTarget.style.transform = "scale(0.96)"}
      onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}
      {...rest}
    >
      {children}
      {badge > 0 && <span style={{ position: "absolute", top: 8, right: 12, background: T.danger, color: "#fff", borderRadius: 12, padding: "3px 10px", fontSize: 14, fontWeight: 800 }}>{badge}</span>}
    </button>
  );
};

const Shell = ({ children, title }) => (
  <div style={{ minHeight: "100vh", background: T.bg, color: T.ink }}>
    <header style={{ padding: "14px 20px", borderBottom: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: 10, letterSpacing: ".3em", color: T.gold }}>CAMASA</div>
        <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18 }}>{title || "Modo Campo"}</div>
      </div>
      <Link to="/campo" style={{ color: T.gold, fontSize: 11, letterSpacing: ".15em", textTransform: "uppercase", textDecoration: "none" }}>← Campo</Link>
    </header>
    <main style={{ maxWidth: 720, margin: "0 auto", padding: 20 }}>{children}</main>
  </div>
);

// ============================================================================
// Home
// ============================================================================

export function ModoCampoHome() {
  const [count, setCount] = useState(0);
  const { api } = useAuth();
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await api("get", "/admin/partner-updates?status=pending");
        if (mounted) setCount((data || []).length);
      } catch { /* */ }
    };
    load();
    const i = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(i); };
  }, [api]);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.ink }}>
      <header style={{ padding: "20px", textAlign: "center", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ fontSize: 11, letterSpacing: ".35em", color: T.gold }}>CAMASA · CAMPO</div>
        <h1 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 28, margin: "8px 0 0 0" }}>Modo Campo</h1>
      </header>
      <main style={{ maxWidth: 640, margin: "0 auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <Link to="/campo/aprovacoes" style={{ textDecoration: "none" }}>
          <Big color="red" badge={count} data-testid="campo-btn-aprovacoes">🔔 Aprovações</Big>
        </Link>
        <Link to="/campo/foto" style={{ textDecoration: "none" }}>
          <Big color="gold" data-testid="campo-btn-foto">📷 Foto Rápida</Big>
        </Link>
        <Link to="/campo/etapa" style={{ textDecoration: "none" }}>
          <Big color="ghost" data-testid="campo-btn-etapa">✏ Atualizar Etapa</Big>
        </Link>
        <Link to="/" style={{ textAlign: "center", marginTop: 24, color: T.ink3, fontSize: 13, textDecoration: "underline" }}>Ver CPS completo →</Link>
      </main>
    </div>
  );
}

// ============================================================================
// Foto Rápida
// ============================================================================

export function ModoCampoFoto() {
  const { api, token } = useAuth();
  const nav = useNavigate();
  const [obras, setObras] = useState([]);
  const [obraId, setObraId] = useState("");
  const [etapa, setEtapa] = useState("");
  const [legenda, setLegenda] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  useEffect(() => {
    api("get", "/obras").then(d => setObras(d || [])).catch(() => {});
  }, [api]);

  const obra = obras.find(o => o.id === obraId);

  const publish = async () => {
    if (!file || !obraId || !etapa) return alert("Foto + obra + etapa obrigatórios");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("etapa", etapa);
      if (legenda) fd.append("legenda", legenda);
      await axios.post(`${API}/obras/${obraId}/upload-foto`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      alert("Foto publicada!");
      nav("/campo");
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao publicar");
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Foto Rápida">
      <input ref={ref} type="file" accept="image/*" capture="environment" onChange={e => setFile(e.target.files?.[0])} style={{ display: "none" }} />
      {!file ? (
        <Big color="gold" onClick={() => ref.current?.click()} data-testid="campo-foto-camera">📷 Abrir Câmera</Big>
      ) : (
        <>
          <div style={{ padding: 16, background: T.bg2, border: `1px solid ${T.line}`, fontSize: 14, marginBottom: 12 }}>
            ✓ Foto selecionada: <strong style={{ color: T.gold }}>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
          </div>
          <select value={obraId} onChange={e => { setObraId(e.target.value); setEtapa(""); }} style={sel} data-testid="campo-foto-obra">
            <option value="">— Selecione a obra —</option>
            {obras.map(o => <option key={o.id} value={o.id}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo} · {o.nome}</option>)}
          </select>
          {obraId && (
            <select value={etapa} onChange={e => setEtapa(e.target.value)} style={sel} data-testid="campo-foto-etapa">
              <option value="">— Selecione a etapa —</option>
              {(obra?.etapas || []).map((e, i) => <option key={i} value={e.nome}>{e.nome}</option>)}
            </select>
          )}
          <input value={legenda} onChange={e => setLegenda(e.target.value)} placeholder="Legenda (opcional)" style={sel} />
          <Big color="green" disabled={busy || !obraId || !etapa} onClick={publish} data-testid="campo-foto-publicar">{busy ? "Publicando…" : "Publicar"}</Big>
          <Big color="ghost" onClick={() => setFile(null)}>Trocar foto</Big>
        </>
      )}
    </Shell>
  );
}

// ============================================================================
// Atualizar Etapa
// ============================================================================

export function ModoCampoEtapa() {
  const { api } = useAuth();
  const [obras, setObras] = useState([]);
  const [obra, setObra] = useState(null);
  const [etapa, setEtapa] = useState(null);
  const [etapaIdx, setEtapaIdx] = useState(-1);
  const [status, setStatus] = useState("em_andamento");
  const [dInicio, setDInicio] = useState("");
  const [dFim, setDFim] = useState("");
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("get", "/obras").then(d => setObras(d || [])).catch(() => {});
  }, [api]);

  const pickEtapa = (e, i) => {
    setEtapa(e); setEtapaIdx(i);
    setStatus(e.status || "aguardando");
    setDInicio(e.data_inicio || "");
    setDFim(e.data_fim || e.data_conclusao || "");
    setObs(e.observacoes || "");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api("post", `/obras/${obra.id}/etapa/${etapaIdx}`, {
        status, data_inicio: dInicio || null, data_fim: dFim || null, observacoes: obs,
      });
      alert("Etapa atualizada!");
      setObra(null); setEtapa(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  if (etapa) return (
    <Shell title={`Etapa: ${etapa.nome}`}>
      <button onClick={() => setEtapa(null)} style={btnBack}>← Voltar</button>
      <div style={{ fontSize: 14, color: T.ink2, marginBottom: 12 }}>{obra.nome}</div>
      <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
        <option value="aguardando">Aguardando</option>
        <option value="em_andamento">Em Andamento</option>
        <option value="concluida">Concluída</option>
      </select>
      <label style={lbl}>Início</label>
      <input type="date" value={dInicio || ""} onChange={e => setDInicio(e.target.value)} style={sel} />
      <label style={lbl}>Fim</label>
      <input type="date" value={dFim || ""} onChange={e => setDFim(e.target.value)} style={sel} />
      <textarea value={obs} onChange={e => setObs(e.target.value)} placeholder="Observações" rows={3} style={tx} />
      <Big color="green" disabled={busy} onClick={save} data-testid="campo-etapa-salvar">{busy ? "Salvando…" : "Salvar"}</Big>
    </Shell>
  );

  if (obra) return (
    <Shell title={obra.nome}>
      <button onClick={() => setObra(null)} style={btnBack}>← Voltar</button>
      {(obra.etapas || []).map((e, i) => (
        <div key={i} onClick={() => pickEtapa(e, i)} style={cardStyle} data-testid={`campo-etapa-item-${i}`}>
          <div style={{ fontSize: 11, color: T.gold, textTransform: "uppercase", letterSpacing: ".15em" }}>{e.status || "aguardando"}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{e.nome}</div>
        </div>
      ))}
    </Shell>
  );

  return (
    <Shell title="Atualizar Etapa">
      {obras.map(o => (
        <div key={o.id} onClick={() => setObra(o)} style={cardStyle} data-testid={`campo-obra-item-${o.id}`}>
          <div style={{ fontSize: 11, color: T.gold, textTransform: "uppercase", letterSpacing: ".15em" }}>{o.numero_orcamento ? `CPS-${o.numero_orcamento}` : o.codigo}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{o.nome}</div>
        </div>
      ))}
    </Shell>
  );
}

// ============================================================================
// Aprovações
// ============================================================================

export function ModoCampoAprovacoes() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await api("get", "/admin/partner-updates?status=pending");
      setItems(data || []);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!confirm("Aprovar este update?")) return;
    await api("patch", `/admin/partner-updates/${id}/approve`, {});
    setItems(prev => prev.filter(x => x.id !== id));
  };

  const reject = async () => {
    if (!motivo.trim()) return alert("Motivo obrigatório");
    await api("patch", `/admin/partner-updates/${rejecting}/reject`, { motivo_rejeicao: motivo });
    setItems(prev => prev.filter(x => x.id !== rejecting));
    setRejecting(null); setMotivo("");
  };

  return (
    <Shell title={`Aprovações (${items.length})`}>
      {loading && <p style={{ color: T.ink3 }}>Carregando…</p>}
      {!loading && items.length === 0 && <p style={{ color: T.ink3, textAlign: "center", padding: 40 }}>✓ Sem pendências</p>}
      {items.map(it => (
        <div key={it.id} style={{ background: T.bg2, border: it.urgente ? `2px solid ${T.danger}` : `1px solid ${T.line}`, padding: 16, marginBottom: 12 }} data-testid={`campo-aprov-${it.id}`}>
          {it.urgente && <div style={{ background: T.danger, color: "#fff", padding: "4px 10px", display: "inline-block", fontSize: 10, letterSpacing: ".2em", marginBottom: 8 }}>URGENTE</div>}
          <div style={{ fontSize: 11, letterSpacing: ".15em", color: T.gold, textTransform: "uppercase" }}>
            {it.parceiro_nome} · {it.parceiro_tipo}
          </div>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, margin: "4px 0" }}>{it.obra_label}</div>
          <div style={{ fontSize: 13, color: T.ink2, marginBottom: 8 }}><strong>{it.tipo_update}</strong></div>
          <pre style={{ fontSize: 12, color: T.ink2, background: T.bg, padding: 10, border: `1px solid ${T.line}`, overflow: "auto", maxHeight: 160, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(it.dados, null, 2)}</pre>
          {(it.dados?.fotos || []).slice(0, 4).map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginRight: 6, marginTop: 6 }}>
              <img src={u} alt="" style={{ width: 70, height: 70, objectFit: "cover", border: `1px solid ${T.line}` }} />
            </a>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => setRejecting(it.id)} style={btnRej} data-testid={`campo-aprov-rejeitar-${it.id}`}>Rejeitar</button>
            <button onClick={() => approve(it.id)} style={btnAprov} data-testid={`campo-aprov-aprovar-${it.id}`}>Aprovar</button>
          </div>
        </div>
      ))}
      {rejecting && (
        <div onClick={() => { setRejecting(null); setMotivo(""); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", padding: 20, zIndex: 9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, padding: 20, border: `1px solid ${T.line}`, width: "100%", maxWidth: 500 }}>
            <h3 style={{ color: T.danger, marginBottom: 12 }}>Rejeitar update</h3>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo (obrigatório)" rows={4} style={tx} autoFocus />
            <Big color="red" onClick={reject} data-testid="campo-aprov-confirmar-rejeicao">Confirmar Rejeição</Big>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ============================================================================
// Estilos
// ============================================================================

const sel = { width: "100%", padding: "16px 18px", fontSize: 18, background: T.bg2, color: T.ink, border: `1px solid ${T.line}`, marginBottom: 12 };
const tx = { width: "100%", padding: 12, fontSize: 16, background: T.bg2, color: T.ink, border: `1px solid ${T.line}`, marginBottom: 12, fontFamily: "inherit", resize: "vertical" };
const lbl = { fontSize: 10, letterSpacing: ".18em", color: T.ink3, textTransform: "uppercase", display: "block", marginBottom: 4 };
const cardStyle = { background: T.bg2, border: `1px solid ${T.line}`, padding: 18, marginBottom: 10, cursor: "pointer" };
const btnBack = { background: "transparent", border: "none", color: T.gold, fontSize: 14, marginBottom: 16, cursor: "pointer" };
const btnRej = { flex: 1, background: T.danger, color: "#fff", border: "none", padding: "14px", fontSize: 14, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer" };
const btnAprov = { flex: 1, background: T.ok, color: "#fff", border: "none", padding: "14px", fontSize: 14, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer" };
